<template>
  <v-dialog
    :value="value"
    @input="$emit('input', $event)"
    max-width="900px"
    persistent
    scrollable
  >
    <v-card>
      <v-card-title class="primary white--text">
        <v-icon color="white" class="mr-2">mdi-format-list-numbered</v-icon>
        Manage Entry Tiers - {{ campaign && campaign.title }}
        <v-spacer></v-spacer>
        <v-btn icon @click="close" color="black">
          <v-icon class="material-icons-round">close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-6">
        <!-- Add New Tier Button -->
        <div class="d-flex justify-space-between align-center mb-6">
          <h3 class="text-h6">Entry Tiers</h3>
          <v-btn
            color="blue"
            dark
            @click="addNewTier"
            :disabled="loading"
          >
            <v-icon left class="material-icons-round">add</v-icon>
            Add Tier
          </v-btn>
        </div>

        <!-- Tiers List -->
        <v-row v-if="tiers.length > 0">
          <v-col
            v-for="(tier, index) in tiers"
            :key="tier.id || index"
            cols="12"
            md="6"
            lg="4"
          >
            <v-card
              outlined
              class="tier-card"
              :class="{ 'editing': tier.editing }"
            >
              <v-card-title class="pb-2">
                <v-chip
                  :color="getTierColor(index)"
                  text-color="white"
                  small
                  class="mr-2"
                >
                  Tier {{ index + 1 }}
                </v-chip>
                <v-spacer></v-spacer>
                <div class="tier-actions">
                  <v-btn
                    icon
                    small
                    @click="editTier(tier, index)"
                    v-if="!tier.editing"
                  >
                    <v-icon small class="material-icons-round">edit</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    small
                    color="error"
                    @click="deleteTier(tier, index)"
                    v-if="!tier.editing"
                  >
                    <v-icon small class="material-icons-round">delete</v-icon>
                  </v-btn>
                </div>
              </v-card-title>

              <v-card-text>
                <template v-if="!tier.editing">
                  <!-- Display Mode -->
                  <div class="tier-display">
                    <div class="tier-info mb-3">
                      <h4 class="text-h6 primary--text">{{ tier.name }}</h4>
                      <p class="text-body-2 grey--text mb-2">{{ tier.description }}</p>
                    </div>
                    
                    <div class="tier-stats">
                      <v-row dense>
                        <v-col cols="6">
                          <div class="stat-item">
                            <div class="stat-label">Price</div>
                            <div class="stat-value">${{ tier.price }}</div>
                          </div>
                        </v-col>
                        <v-col cols="6">
                          <div class="stat-item">
                            <div class="stat-label">Entries</div>
                            <div class="stat-value">{{ tier.entries }}</div>
                          </div>
                        </v-col>
                      </v-row>
                    </div>

                    <v-chip
                      :color="'green'"
                      text-color="white"
                      small
                      class="mt-2"
                    >
                      Active
                    </v-chip>
                  </div>
                </template>

                <template v-else>
                  <!-- Edit Mode -->
                  <v-form ref="tierForm" v-model="tier.valid">
                    <v-text-field
                      v-model="tier.name"
                      label="Tier Name"
                      :rules="[rules.required]"
                      outlined
                      dense
                      class="mb-2"
                    ></v-text-field>

                    <v-textarea
                      v-model="tier.description"
                      label="Description"
                      :rules="[rules.required]"
                      outlined
                      rows="2"
                      dense
                      class="mb-2"
                    ></v-textarea>

                    <v-row dense>
                      <v-col cols="6">
                        <v-text-field
                          v-model="tier.price"
                          label="Price ($)"
                          :rules="[rules.required, rules.number]"
                          outlined
                          dense
                          type="number"
                          step="0.01"
                        ></v-text-field>
                      </v-col>
                      <v-col cols="6">
                        <v-text-field
                          v-model="tier.entries"
                          label="Entries"
                          :rules="[rules.required, rules.integer]"
                          outlined
                          dense
                          type="number"
                        ></v-text-field>
                      </v-col>
                    </v-row>

                    <v-switch
                      v-model="tier.active"
                      label="Active"
                      color="primary"
                      dense
                    ></v-switch>

                    <div class="d-flex justify-end mt-3">
                      <v-btn
                        text
                        small
                        @click="cancelEdit(tier, index)"
                        class="mr-2"
                      >
                        Cancel
                      </v-btn>
                      <v-btn
                        color="blue"
                        small
                        dark
                        @click="saveTier(tier, index)"
                        :loading="tier.saving"
                        :disabled="!tier.valid"
                      >
                        Save
                      </v-btn>
                    </div>
                  </v-form>
                </template>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Empty State -->
        <v-card v-else outlined class="text-center pa-8">
          <v-icon size="64" color="grey lighten-1" class="mb-4">mdi-format-list-numbered</v-icon>
          <h3 class="text-h6 grey--text mb-2">No Entry Tiers</h3>
          <p class="grey--text mb-4">Create entry tiers to allow users to participate in this sweepstakes.</p>
          <v-btn color="primary" @click="addNewTier">
            <v-icon left>mdi-plus</v-icon>
            Add First Tier
          </v-btn>
        </v-card>
      </v-card-text>

      <v-card-actions class="pa-6">
        <v-spacer></v-spacer>
        <v-btn
          text
          @click="close"
        >
          Close
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'SweepstakesTiersDialog',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    campaign: {
      type: Object,
      default: () => null
    }
  },
  data() {
    return {
      loading: false,
      tiers: [],
      rules: {
        required: value => !!value || 'This field is required',
        number: value => !isNaN(value) && value >= 0 || 'Must be a valid number',
        integer: value => Number.isInteger(Number(value)) && value > 0 || 'Must be a positive integer'
      }
    }
  },
  watch: {
    value(newVal) {
      if (newVal && this.campaign) {
        this.loadTiers()
      }
    }
  },
  methods: {
    async loadTiers() {
      try {
        this.loading = true
        // Try to use admin API to get tiers
        const response = await window.axios.get(`/api/sweepstakes-admin/campaigns/${this.campaign.id}/tiers`, {
          headers: {
            Authorization: "Bearer " + localStorage.getItem('token')
          }
        })
        if (response.data.flag === 1) {
          this.tiers = response.data.tiers.map(tier => ({
            ...tier,
            editing: false,
            saving: false,
            valid: true
          }))
        } else {
          this.tiers = []
        }
      } catch (error) {
        console.error('Error loading tiers:', error)
        this.$toast.error('Failed to load tiers')
        this.tiers = []
      } finally {
        this.loading = false
      }
    },

    addNewTier() {
      const newTier = {
        id: null,
        name: '',
        description: '',
        price: '',
        entries: '',
        active: true,
        editing: true,
        saving: false,
        valid: false,
        isNew: true
      }
      this.tiers.push(newTier)
    },

    editTier(tier, index) {
      // Store original values for cancel functionality
      tier.originalValues = {
        name: tier.name,
        description: tier.description,
        price: tier.price,
        entries: tier.entries,
        active: tier.active
      }
      tier.editing = true
    },

    cancelEdit(tier, index) {
      if (tier.isNew) {
        this.tiers.splice(index, 1)
      } else {
        // Restore original values
        Object.assign(tier, tier.originalValues)
        tier.editing = false
        delete tier.originalValues
      }
    },

    async saveTier(tier, index) {
      if (!tier.valid) return

      try {
        tier.saving = true
        
        const tierData = {
          campaign_id: this.campaign.id,
          name: tier.name,
          description: tier.description,
          price: parseFloat(tier.price),
          entries: parseInt(tier.entries),
          active: tier.active
        }

        // Tier management API not available yet
        this.$toast.error('Tier management API not implemented yet')
        tier.editing = false
        if (tier.originalValues) {
          Object.assign(tier, tier.originalValues)
          delete tier.originalValues
        }
        // TODO: Implement actual API calls when admin endpoints are available
      } catch (error) {
        console.error('Error saving tier:', error)
        this.$toast.error('Failed to save tier')
      } finally {
        tier.saving = false
      }
    },

    async deleteTier(tier, index) {
      if (await this.$confirm('Are you sure you want to delete this tier?')) {
        try {
          // Tier deletion API not available yet
          this.$toast.error('Tier deletion API not implemented yet')
          // TODO: Implement actual API call when admin endpoints are available
        } catch (error) {
          console.error('Error deleting tier:', error)
          this.$toast.error('Failed to delete tier')
        }
      }
    },

    getTierColor(index) {
      const colors = ['primary', 'success', 'warning', 'info', 'purple']
      return colors[index % colors.length]
    },

    close() {
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
.tier-card {
  transition: all 0.3s ease;
  height: 100%;
}

.tier-card.editing {
  border-color: #1976d2;
  box-shadow: 0 4px 12px rgba(25, 118, 210, 0.2);
}

.tier-actions {
  display: flex;
  gap: 4px;
}

.tier-display {
  min-height: 120px;
}

.stat-item {
  text-align: center;
}

.stat-label {
  font-size: 0.75rem;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.stat-value {
  font-size: 1.25rem;
  font-weight: bold;
  color: #1976d2;
}
</style>
